import { useEffect, useRef, useState } from 'react';
import { continueBase, dictationBase, joinDictation } from '../engine/dictationText';

/**
 * Shared voice-dictation hook wrapping the Web Speech API with the
 * platform hardening every input field needs:
 *
 * - event.results is the FULL transcript of a recognition run, and some
 *   engines (notably Chrome on Android) re-emit the same FINAL phrase
 *   several times and send cumulative interims. We rebuild the text from
 *   scratch on every event: finals with consecutive duplicates dropped,
 *   plus only the newest interim — never append.
 * - Chrome on Android mangles continuous mode (duplicated results, capture
 *   that silently stops). There we run one utterance at a time and
 *   auto-restart in onend so dictation still feels continuous.
 */

/** Minimal typing for the Web Speech API (not in lib.dom for all targets). */
interface SpeechResultLike {
  0: { transcript: string };
  isFinal: boolean;
}
interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: ((event: { results: ArrayLike<SpeechResultLike> }) => void) | null;
  onend: (() => void) | null;
  onerror: ((event: { error?: string }) => void) | null;
  start(): void;
  stop(): void;
}

const VOICE_ERRORS: Record<string, string> = {
  'not-allowed':
    'Microphone access is blocked. Allow the mic permission for this site in your browser settings, then try again.',
  'service-not-allowed':
    'Speech recognition is blocked on this device — type your list instead, or use the camera scanner.',
  'audio-capture': 'No microphone was found on this device.',
  'no-speech': "Didn't catch anything — tap the mic and try speaking again.",
  network: 'The speech service needs an internet connection — check your connection and retry.',
};

function getSpeechRecognition(): (new () => SpeechRecognitionLike) | undefined {
  const w = window as unknown as Record<string, unknown>;
  return (w.SpeechRecognition ?? w.webkitSpeechRecognition) as
    | (new () => SpeechRecognitionLike)
    | undefined;
}

export interface Dictation {
  /** Whether the Web Speech API exists in this browser. */
  supported: boolean;
  /** True while the mic is (or is auto-restarting to stay) live. */
  listening: boolean;
  /** User-facing problem message, or null when all is well. */
  notice: string | null;
  /**
   * Start or stop dictation. Pass the field's current text so the
   * dictated words append after it instead of replacing it.
   */
  toggle: (currentText: string) => void;
}

/** @param onText Receives the full rebuilt field text on every speech event. */
/**
 * @param opts.lines true only for the brain-dump box: each mic tap and each
 *   spoken "next item" starts a new line (one line = one task). Every other
 *   field is single-line and keeps one space-joined line, as before.
 */
export function useDictation(onText: (text: string) => void, opts: { lines?: boolean } = {}): Dictation {
  const [listening, setListening] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  /** Text that existed before this dictation session started. */
  const baseTextRef = useRef('');
  /** True while the user wants the mic on (drives auto-restart on mobile). */
  const wantListeningRef = useRef(false);
  /** The deduped transcript of the current recognition run. */
  const sessionTextRef = useRef('');
  /** Latest onText without stale closures. */
  const lines = opts.lines === true;
  const join = (base: string, session: string) =>
    lines ? joinDictation(base, session) : `${base}${session}`.replace(/\s+/g, ' ').trimEnd();
  const onTextRef = useRef((t: string) => onText(lines ? t : t.replace(/\s+/g, ' ').trim()));
  onTextRef.current = (t: string) => onText(lines ? t : t.replace(/\s+/g, ' ').trim());

  const supported = typeof window !== 'undefined' && getSpeechRecognition() !== undefined;

  useEffect(
    () => () => {
      wantListeningRef.current = false;
      recognitionRef.current?.stop();
    },
    [],
  );

  const toggle = (currentText: string) => {
    if (wantListeningRef.current) {
      wantListeningRef.current = false;
      recognitionRef.current?.stop();
      setListening(false);
      return;
    }
    const Ctor = getSpeechRecognition();
    if (!Ctor) {
      setNotice('Voice input isn’t supported in this browser — please type instead.');
      return;
    }
    setNotice(null);
    // Each tap of the mic starts a new line (engine/dictationText.ts).
    baseTextRef.current = lines
      ? dictationBase(currentText)
      : currentText.trim() ? `${currentText.trim()} ` : '';
    sessionTextRef.current = '';
    const rec = new Ctor();
    rec.lang = navigator.language || 'en-US';
    const isAndroid = /android/i.test(navigator.userAgent);
    rec.continuous = !isAndroid;
    rec.interimResults = true;
    rec.onresult = (event) => {
      const finals: string[] = [];
      let interim = '';
      for (let i = 0; i < event.results.length; i += 1) {
        const r = event.results[i];
        const t = (r[0]?.transcript ?? '').replace(/\s+/g, ' ').trim();
        if (!t) continue;
        if (r.isFinal) {
          if (finals[finals.length - 1]?.toLowerCase() !== t.toLowerCase()) finals.push(t);
        } else {
          interim = t;
        }
      }
      // Some engines echo the just-finalized phrase as a trailing interim.
      if (interim && finals[finals.length - 1]?.toLowerCase() === interim.toLowerCase()) {
        interim = '';
      }
      sessionTextRef.current = [...finals, interim].filter(Boolean).join(' ');
      // One-line fields keep the words as spoken ("… next item …") — Quick
      // Task splits on them when added (splitQuickTasks).
      onTextRef.current?.(join(baseTextRef.current ?? '', sessionTextRef.current ?? ''));
    };
    rec.onend = () => {
      if (wantListeningRef.current) {
        // Fold the finished utterance into the base, then restart so the
        // next utterance appends instead of overwriting (mobile mode).
        // A pause on Android ends the utterance; the next one continues the
        // SAME line (say "next item" or tap the mic again for a new item).
        const merged = join(baseTextRef.current ?? '', sessionTextRef.current ?? '');
        baseTextRef.current = continueBase(merged);
        sessionTextRef.current = '';
        try {
          rec.start();
          return;
        } catch {
          // Restart refused — fall through and stop cleanly.
        }
      }
      wantListeningRef.current = false;
      setListening(false);
    };
    rec.onerror = (event) => {
      wantListeningRef.current = false;
      setListening(false);
      // 'aborted' fires on normal stop — not an error worth surfacing.
      if (event.error === 'aborted') return;
      // A 'no-speech' timeout AFTER words were captured is just the engine
      // ending a successful dictation on silence — never show an error for
      // a session that worked.
      if (event.error === 'no-speech' && (sessionTextRef.current ?? '').trim()) return;
      const msg = VOICE_ERRORS[event.error ?? ''];
      setNotice(msg ?? 'Voice input hit a problem — please try again or type instead.');
    };
    recognitionRef.current = rec;
    try {
      rec.start();
      wantListeningRef.current = true;
      setListening(true);
    } catch {
      setNotice('Couldn’t start the microphone — please try again.');
    }
  };

  return { supported, listening, notice, toggle };
}
